import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { X, Sparkles, Calendar as CalIcon, ArrowRight } from "lucide-react";

// Homecoming Dress Event · Aug 1-2, 2026 · Lake Country DockHounds
// Non-intrusive: appears after 8s idle on the FIRST visit only, dismissible
// (session-remembered), auto-suppressed on the event page, cart, checkout,
// auth pages, and admin.
const DISMISS_KEY = "hb_event_popup_dismissed";
const HIDDEN_PATHS = ["/events/", "/cart", "/checkout", "/thank-you", "/auth", "/admin", "/family/accept"];

export function EventPopup() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Suppress on excluded routes
    const suppressedRoute = HIDDEN_PATHS.some((p) => window.location.pathname.startsWith(p));
    if (suppressedRoute) return;
    // Session dismiss
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch { /* noop */ }
    const t = setTimeout(() => setVisible(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const go = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    navigate("/events/homecoming-2026");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ x: 40, y: 40, opacity: 0, scale: 0.96 }}
          animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          exit={{ x: 40, y: 40, opacity: 0, scale: 0.96 }}
          transition={{ type: "spring", damping: 24, stiffness: 260 }}
          className="fixed z-[65] bottom-6 left-6 max-w-[340px] w-[calc(100%-3rem)] sm:w-[340px]"
          data-testid="event-popup"
        >
          <div className="relative rounded-sm overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] border border-rosemetal/30">
            {/* Decorative ballpark gradient — pink + gold night sky */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#3d0f1f] via-ink to-[#5a1030]" />
            <div className="absolute -top-8 -right-6 w-32 h-32 rounded-full bg-[#FF6B9E]/40 blur-2xl" aria-hidden />
            <div className="absolute -bottom-10 -left-4 w-32 h-32 rounded-full bg-champagne/25 blur-2xl" aria-hidden />

            <button
              onClick={dismiss}
              aria-label="Dismiss"
              data-testid="event-popup-close"
              className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/40 text-ivory/80 hover:bg-black/70 hover:text-ivory transition-colors flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5" />
            </button>

            <div className="relative p-5 text-ivory">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FF6B9E]/20 border border-[#FF6B9E]/40 rounded-full mb-3">
                <Sparkles className="w-3 h-3 text-[#FF6B9E]" />
                <span className="font-label text-[8px] tracking-[0.3em] text-[#FF6B9E]">TWO DAYS ONLY</span>
              </div>
              <p className="font-display italic text-lg text-champagne/90 leading-none">Heather's</p>
              <h3 className="font-display text-[36px] leading-[0.9] mt-1 text-[#FF6B9E] tracking-tight">
                Homecoming<br />Dress Event
              </h3>
              <p className="font-label text-[10px] tracking-[0.25em] mt-3 text-ivory">
                AUG 1<span className="text-[#FF6B9E]">·</span>2 · LAKE COUNTRY DOCKHOUNDS
              </p>
              <div className="mt-4 pt-4 border-t border-ivory/15 flex items-baseline justify-between gap-3">
                <div>
                  <p className="font-label text-[9px] tracking-widest text-ivory/60">FROM</p>
                  <p className="font-display text-3xl text-[#FF6B9E] leading-none">$99</p>
                </div>
                <div className="text-right">
                  <p className="font-label text-[9px] tracking-widest text-ivory/60">FIRST 25</p>
                  <p className="font-body text-xs text-champagne">VIP Bag 🎁</p>
                </div>
              </div>
              <button
                onClick={go}
                data-testid="event-popup-cta"
                className="mt-5 w-full bg-[#FF6B9E] hover:bg-ivory text-ink font-label text-[10px] tracking-[0.3em] py-3 rounded-sm transition-colors inline-flex items-center justify-center gap-2 group"
              >
                SAVE MY SPOT <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={dismiss}
                data-testid="event-popup-later"
                className="mt-1.5 w-full text-center font-label text-[9px] tracking-widest text-ivory/40 hover:text-ivory py-1"
              >
                MAYBE LATER
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
